/**
 * 使い方ガイド（契約者パネル内のヘルプ）。
 *
 * **これまで製品の中にヘルプが 1 枚も無かった。** ヘッダの「ヘルプ」は
 * `VITE_SUPPORT_URL` が設定されているときだけ外部サイトへ飛ぶボタンで、
 * 未設定の構成では**どこからもヘルプに辿り着けない**（`env.ts:15` の
 * 「空なら導線を出さない」がそのまま「ヘルプが存在しない」になっていた）。
 * LP 側の `apps/lp/help.html` は申し込む前の人向けで、ログイン後の画面からは
 * リンクされていない。
 *
 * ## 何を書くか
 *
 * **画面の使い方の再掲はしない。** 各画面には既に `HelpNote` / `Disclosure` で
 * その場の説明が入っている。ここに要るのは
 *   - 初めて使う人が「次に何をすればいいか」順番に辿れること
 *   - 機能があること自体を知らない人に、機能の存在を伝えること
 * の 2 つ。とくに後者は、プライベートアドレス・外部画像の代理取得・AI の
 * 3 つが新しく入ったのに、契約者から見て「ナビに項目が増えた」以上の
 * 説明がどこにも無い状態だった。
 *
 * ## 金額・日数をこのファイルに書かない
 *
 * 税率と無料お試しの日数は `GET /public/plans` が返す値（サーバが正）を
 * そのまま出す。ここに数字を直書きすると、法定ページ（特商法・利用規約）と
 * 食い違ったときに**画面だけが古い数字を言い続ける**。総額表示（消費税法 63 条）も
 * 同じ理由で、税込を主に出す方針をサーバの値で担保する。
 */
import type * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  PageHeader,
  Section,
  formatCount,
} from '@mail-sv/ui';
import {
  AtSign,
  Bot,
  CreditCard,
  EyeOff,
  Globe,
  Mail,
  VenetianMask,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { publicApi } from '../lib/api.js';
import { AppLink } from '../lib/nav.js';
import { useOrg } from '../lib/hooks.js';
import { Disclosure, HelpNote, StepList } from '../components/help.js';
import { env } from '../env.js';

/** `GET /public/plans` のうち、この画面が使う値だけ。税率と試用日数はサーバが正。 */
interface PublicPlansResponse {
  taxRateBp: number;
  trialDays: number;
}

export function HelpPage() {
  const org = useOrg();
  const plans = useQuery({
    queryKey: ['public', 'plans'],
    queryFn: () => publicApi.get<PublicPlansResponse>('/plans'),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const features = org.data?.plan.features;

  return (
    <>
      <PageHeader
        title="使い方ガイド"
        description="はじめての設定から、届かないときの調べ方まで。各項目から実際の画面へ移動できます。"
      />

      <div className="space-y-6">
        <HelpNote title="困ったときの順番">
          ①この画面で当てはまる項目を開く → ②書かれている画面で状況を確認する →
          ③それでも解決しないときは
          <AppLink to="/support" className="mx-1 text-primary underline-offset-4 hover:underline">
            サポート
          </AppLink>
          へお問い合わせください。お問い合わせの前に状況を確認しておくと、やり取りが 1
          往復で済みます。
        </HelpNote>

        <Section
          title="はじめの設定"
          description="この順番どおりに進めると、メールが使えるようになります。"
        >
          <Card>
            <CardContent className="space-y-4 p-5">
              <StepList
                steps={[
                  'ドメインを登録します（メールアドレスの「@」より後ろに使う名前です）。',
                  '表示された DNS レコードを、ドメインを管理している事業者の画面に登録します。ここが終わるまでメールは届きません。',
                  'メールアドレスを作成します（info@ や support@ など、実際に使うアドレス）。',
                  'Webメールにログインするか、お使いのメールソフトに設定します。',
                  'お支払い方法を登録します。無料お試し期間の終了後に送信が止まらないようにするための手続きです。',
                ]}
              />
              <div className="flex flex-wrap gap-2 text-sm">
                <GuideLink to="/domains" icon={Globe} label="ドメイン" />
                <GuideLink to="/mailboxes" icon={AtSign} label="メールアドレス" />
                <GuideLink to="/mail-clients" icon={Mail} label="メールソフトの設定" />
                <GuideLink to="/billing" icon={CreditCard} label="料金プラン・請求" />
              </div>
            </CardContent>
          </Card>
        </Section>

        <Section
          title="メールが届かない・送れないとき"
          description="まずここに書かれた順で切り分けてください。"
        >
          <div className="space-y-3">
            <Disclosure summary="特定の相手に届いたかどうかを調べたい" defaultOpen>
              <p>
                「メールの履歴」の
                <b>メールの配送記録</b>
                で、差出人・宛先で検索できます。届いた／届かなかったの結果と、相手のサーバーが返した理由がそのまま残っています。
              </p>
              <p className="mt-2">
                件名や本文は記録していません（お預かりしたメールの中身を運営が読めない設計にしているためです）。
              </p>
              <p className="mt-2">
                <AppLink to="/logs" className="text-primary underline-offset-4 hover:underline">
                  メールの履歴を開く
                </AppLink>
              </p>
            </Disclosure>

            <Disclosure summary="ドメイン全体でメールが届かない">
              <p>
                ほとんどの場合、DNS
                の設定が反映されていないか、値が 1 文字違っています。ドメインの詳細画面に「あるべき値」と「今の値」が並んで出ますので、食い違っている行を直してください。反映には数分〜数時間かかることがあります。
              </p>
              <p className="mt-2">
                <AppLink to="/domains" className="text-primary underline-offset-4 hover:underline">
                  ドメインを開く
                </AppLink>
              </p>
            </Disclosure>

            <Disclosure summary="急に送信だけができなくなった">
              <p>次のいずれかに当てはまることが多いです。</p>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                <li>
                  1 日の送信上限に達している →{' '}
                  <AppLink to="/usage" className="text-primary underline-offset-4 hover:underline">
                    容量・送信数
                  </AppLink>
                </li>
                <li>
                  事務所の接続元 IP アドレスが遮断されている（パスワードの間違いが短時間に繰り返されたときに自動で行われます）→{' '}
                  <AppLink
                    to="/security"
                    className="text-primary underline-offset-4 hover:underline"
                  >
                    セキュリティ
                  </AppLink>
                  の画面で遮断中のアドレスを確認し、その場で解除を申請できます
                </li>
                <li>
                  お支払いが確認できず送信が停止している →{' '}
                  <AppLink to="/billing" className="text-primary underline-offset-4 hover:underline">
                    料金プラン・請求
                  </AppLink>
                  （受信は止まりません）
                </li>
              </ul>
            </Disclosure>

            <Disclosure summary="エイリアス宛に届いたメールに、そのエイリアスの名前で返信したい">
              <p>
                差出人として名乗れるアドレスは、なりすまし防止のため既定で本人のアドレスだけに絞っています。メールアドレスの詳細画面の
                <b>送信できる差出人</b>
                タブで、共有アドレスやエイリアスを追加してください。追加するまでは送信が拒否されます。
              </p>
              <p className="mt-2">
                <AppLink
                  to="/mailboxes"
                  className="text-primary underline-offset-4 hover:underline"
                >
                  メールアドレスを開く
                </AppLink>
              </p>
            </Disclosure>

            <Disclosure summary="メールソフトの設定でつまずく">
              <p>
                失敗のほとんどは「ユーザー名をメールアドレス全体にしていない」か「暗号化の種類が違う」のどちらかです。設定画面に、そのまま貼り付けられる値と機種別の手順を用意しています。
              </p>
              <p className="mt-2">
                <AppLink
                  to="/mail-clients"
                  className="text-primary underline-offset-4 hover:underline"
                >
                  メールソフトの設定を開く
                </AppLink>
              </p>
            </Disclosure>
          </div>
        </Section>

        {/*
         * **新しく増えた機能はここで存在を伝える。**
         * ナビに項目が増えただけでは、何ができるようになったのか誰も分からない。
         * プランで使えない契約者にも項目は出す（各画面が理由と変更導線を出す）。
         */}
        <Section
          title="最近ふえた機能"
          description="ご契約のプランによっては、まだお使いいただけない機能もあります。"
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <FeatureCard
              icon={VenetianMask}
              title="プライベートアドレス"
              to="/private-addresses"
              available={(org.data?.plan.limits.privateAddressesMax ?? 0) > 0}
            >
              <p>
                通販サイトや問い合わせフォームに登録するための、使い捨てのアドレスを発行できます。届いたメールはご自身のアドレスへ転送されます。
              </p>
              <p className="mt-2">
                迷惑メールが来はじめたら、そのアドレスだけを止めれば本来のアドレスは無傷です。どこから漏れたのかも分かります。
              </p>
            </FeatureCard>

            <FeatureCard
              icon={EyeOff}
              title="外部画像の代理取得"
              to="/privacy"
              available={features?.imageProxy ?? false}
            >
              <p>
                HTML
                メールに埋め込まれた画像は、開いた瞬間に送信者へ「いつ・どこで・どの端末で開いたか」を伝えます。当社のサーバーが代わりに取得することで、それらが送信者に伝わらなくなります。
              </p>
              <p className="mt-2">
                本文の書き換えは、Webメールをお客様専用のホスト名で公開している場合に行われます。共通の
                Webメール URL のままでは働きませんのでご注意ください（
                <AppLink
                  to="/branding"
                  className="text-primary underline-offset-4 hover:underline"
                >
                  ホスト名の設定
                </AppLink>
                ）。
              </p>
            </FeatureCard>

            <FeatureCard
              icon={Bot}
              title="Webメールの AI"
              to="/ai"
              available={features?.aiAssistant ?? false}
            >
              <p>
                長いメールの要約や、返信文の下書きを Webメールの中で作れます。
                <b>既定ではオフ</b>
                で、有効にするかどうかはお客様が決めます。
              </p>
              <p className="mt-2">
                どの範囲の本文を渡すか、どこの事業者へ渡すか、月にどれだけ使うかも設定画面で指定できます。使用量は
                <AppLink to="/usage" className="mx-1 text-primary underline-offset-4 hover:underline">
                  容量・送信数
                </AppLink>
                でも確認できます。
              </p>
            </FeatureCard>
          </div>
        </Section>

        {/*
         * **効果測定は「何を測っていないか」まで書く。**
         * 一斉配信の画面にはクリックの内訳が出るが、開封が無い理由が
         * どこにも書かれていなかった。他社の配信サービスには必ずある項目
         * なので、無言で欠けていると「壊れている」と読まれる。
         * `tracking_opt_out` の意味も、正確に書かないと契約者が受信者へ
         * 誤った説明をしてしまう（実装の根拠は
         * `apps/worker/src/workers/content-build.ts` の `applyClickTracking`）。
         */}
        <Section
          title="配信の効果を見る"
          description="一斉配信をご利用の場合、配信後の反応をキャンペーンの画面で確認できます。"
        >
          <div className="space-y-3">
            <Disclosure summary="どのリンクが押されたかを見たい" defaultOpen>
              <p>
                宛先リストの設定で
                <b>クリックの計測</b>
                を有効にすると、配信したメールの本文中のリンクが当社のサーバーを経由するようになり、配信後に次のことが分かります。
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                <li>
                  <b>リンクごとの回数</b>
                  。1 度も押されなかったリンクも 0 として表示します（表から消すと「無かった」と読めてしまうためです）。
                </li>
                <li>
                  <b>延べ回数と人数を分けて</b>
                  表示します。同じ方が 5 回押しても 5 人ではありません。
                </li>
                <li>
                  <b>配信からの経過時間ごとの推移</b>
                  。次回の配信時刻を決める材料になります。
                </li>
                <li>
                  <b>CSV の書き出し</b>
                  。要約・リンク別・時系列の 3 種類を、それぞれ別のファイルとして取得できます。お手元の表計算ソフトや BI でお使いください。
                </li>
              </ul>
              <p className="mt-2">
                割合の
                <b>分母は「配信に成功した通数」</b>
                です。宛先の件数ではありません（届かなかった方や、配信を停止された方を「押さなかった人」に数えないためです）。画面にも
                CSV にも分母の数値そのものを表示しています。
              </p>
              <p className="mt-2">
                計測を有効にしていない配信では、数値は表示せず「計測していません」と表示します。
                <b>0 回と表示することはありません</b>
                （押されなかったのか、記録していないのかが区別できなくなるためです）。
              </p>
              <p className="mt-2">
                <AppLink to="/bulk/lists" className="text-primary underline-offset-4 hover:underline">
                  宛先リストの設定を開く
                </AppLink>
                <span className="mx-2 text-muted-foreground">/</span>
                <AppLink
                  to="/bulk/campaigns"
                  className="text-primary underline-offset-4 hover:underline"
                >
                  一斉配信を開く
                </AppLink>
              </p>
            </Disclosure>

            <Disclosure summary="開封率が見当たらない">
              <p>
                <b>当社では開封を計測していません。</b>
                他社のサービスにある「開封率」は、本文に埋め込んだ見えない画像を受信者が読み込んだかどうかで測るものです。
              </p>
              <p className="mt-2">
                当社は、お客様がお使いの Webメールで
                <b>外部画像を当社が代わりに取得し、送信者に「いつ・どこで・どの端末で開いたか」を伝えない</b>
                機能を提供し、そのことを法定のページでお約束しています。自社の配信でだけ同じ仕組みの画像を配るのは、そのお約束と正面から矛盾します。
              </p>
              <p className="mt-2">
                加えて、仮に配ったとしても当社の代理取得の仕組みが画像を保存してしまうため、数字としても正しく機能しません。
                代わりに
                <b>クリック</b>
                と
                <b>返信</b>
                を反応の指標としてお使いください。
              </p>
            </Disclosure>

            <Disclosure summary="返信の数はどう扱われますか">
              <p>
                配信したメールへの返信は、お客様のメールボックスに通常のメールとして届きます。受信も当社のサーバーで行っているため、受信者に追跡用の画像を配らなくても「反応があったかどうか」を測れる指標になります。
              </p>
              <p className="mt-2">
                <b>ただし現時点では、返信をキャンペーンの集計へ自動で結び付ける機能はまだ提供していません。</b>
                そのためキャンペーンの画面では返信の件数を「—」と表示し、
                <b>0 件とは表示しません</b>
                （0 と書くと「誰も返信しなかった」と読めてしまうためです）。返信そのものは通常どおり受信できています。
              </p>
            </Disclosure>

            <Disclosure summary="「追跡を拒否」した受信者はどう扱われますか">
              <p>
                受信者ごとに
                <b>追跡の拒否</b>
                を設定できます（宛先リストの連絡先の編集画面）。設定した方については、リンクが押されても
                <b>記録を一切残さず</b>
                、集計・レポート・CSV のどこにも現れません。
              </p>
              <p className="mt-2">
                <b>意味を正確にお伝えします。</b>
                この設定は「この方のクリックを
                <b>記録しない</b>
                」という意味であって、「この方のリンクが
                <b>当社のサーバーを経由しない</b>
                」という意味ではありません。本文はキャンペーンにつき 1 つしか作らないため、リンクの宛先は受信者によらず同じ（当社を経由する形）になります。当社のサーバーは受け取った時点で何も記録せず、そのまま本来のページへ転送します。
              </p>
              <p className="mt-2">
                受信者の方へご説明される際は、この違いにご注意ください。なお、リンクは転送されるので
                <b>リンクが壊れることはありません</b>
                。配信停止の導線（法令で必須です）も同じ仕組みを使っているため、追跡の拒否によって解除できなくなることもありません。
              </p>
              <p className="mt-2">
                <AppLink to="/bulk/lists" className="text-primary underline-offset-4 hover:underline">
                  宛先リストを開く
                </AppLink>
              </p>
            </Disclosure>
          </div>
        </Section>

        <Section title="安全に使うために">
          <div className="space-y-3">
            <Disclosure summary="パスワードが漏れたかもしれない">
              <p>
                すべてのメールアドレスのパスワードを一度に再設定できます。セキュリティ画面の「緊急時の操作」からお進みください。1
                件ずつ変更する必要はありません。新しいパスワードはその場で 1
                度だけ表示され、あとから見ることはできませんので、必ず保存してから閉じてください。
              </p>
              <p className="mt-2">
                <AppLink
                  to="/security"
                  className="text-primary underline-offset-4 hover:underline"
                >
                  セキュリティを開く
                </AppLink>
              </p>
            </Disclosure>

            <Disclosure summary="誰が何をしたのかを確認したい">
              <p>
                「メールの履歴」の
                <b>操作の履歴</b>
                で、設定変更・削除・ログインなどの記録を確認できます。運営スタッフがお客様の画面を代理で操作した記録も同じ場所に残ります。
              </p>
              <p className="mt-2">
                <AppLink to="/logs" className="text-primary underline-offset-4 hover:underline">
                  メールの履歴を開く
                </AppLink>
              </p>
            </Disclosure>

            <Disclosure summary="ログインを 2 段階にしたい">
              <p>
                スマートフォンの認証アプリを使った 2
                段階認証を設定できます。組織のメンバー全員に必須にすることもできます。
              </p>
              <p className="mt-2">
                <AppLink
                  to="/security"
                  className="text-primary underline-offset-4 hover:underline"
                >
                  セキュリティを開く
                </AppLink>
              </p>
            </Disclosure>
          </div>
        </Section>

        <Section title="ご契約・お支払い">
          <div className="space-y-3">
            <Disclosure summary="料金の表示について" defaultOpen>
              <ul className="list-disc space-y-1 pl-5">
                <li>
                  画面に出る金額は
                  <b>消費税を含んだ総額</b>
                  です
                  {plans.data ? `（消費税 ${plans.data.taxRateBp / 100}%）` : ''}
                  。税抜の金額は括弧内に併記しています。
                </li>
                <li>
                  年払いをお選びいただくと、月払いより 2 か月分お得になります。お支払い周期は
                  <AppLink
                    to="/billing/plan"
                    className="mx-1 text-primary underline-offset-4 hover:underline"
                  >
                    プランの変更
                  </AppLink>
                  の画面で切り替えられます。
                </li>
                {plans.data && plans.data.trialDays > 0 ? (
                  <li>
                    お申し込みから {formatCount(plans.data.trialDays)}{' '}
                    日間は無料でお試しいただけます。この期間中の料金は発生せず、期間中に解約された場合も課金されません。
                  </li>
                ) : null}
                <li>
                  上位プランへの変更はすぐに、下位プランへの変更は次回更新日に適用されます。上位へ変更したときの差額は次回の請求に反映されます。
                </li>
              </ul>
            </Disclosure>

            <Disclosure summary="請求書の内訳を見たい">
              <p>
                請求の画面で各請求書を開くと、何にいくら課金されたかを税率の区分ごとに確認できます。PDF
                は発行の準備が整いしだいダウンロードできるようになります。
              </p>
              <p className="mt-2">
                <AppLink to="/billing" className="text-primary underline-offset-4 hover:underline">
                  料金プラン・請求を開く
                </AppLink>
              </p>
            </Disclosure>

            <Disclosure summary="解約したい・データを持ち出したい">
              <p>
                解約の前に、メールやお預かりしているデータを書き出せます。書き出しには時間がかかることがありますので、解約のお手続きより先に行ってください。
              </p>
              <p className="mt-2">
                <AppLink to="/exports" className="text-primary underline-offset-4 hover:underline">
                  データの書き出しを開く
                </AppLink>
                {' ・ '}
                <AppLink to="/legal" className="text-primary underline-offset-4 hover:underline">
                  規約・法定のご案内
                </AppLink>
              </p>
            </Disclosure>
          </div>
        </Section>

        {env.supportUrl ? (
          <HelpNote title="この画面で解決しないとき">
            <a
              href={env.supportUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="text-primary underline-offset-4 hover:underline"
            >
              サポート窓口
            </a>
            、または
            <AppLink to="/support" className="mx-1 text-primary underline-offset-4 hover:underline">
              サポート
            </AppLink>
            の画面からお問い合わせください。
          </HelpNote>
        ) : null}
      </div>
    </>
  );
}

function GuideLink({ to, icon: Icon, label }: { to: string; icon: LucideIcon; label: string }) {
  return (
    <AppLink
      to={to}
      className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-sm hover:bg-hover"
    >
      <Icon className="size-4 text-muted-foreground" aria-hidden />
      {label}
    </AppLink>
  );
}

/**
 * 機能の紹介カード。
 *
 * **プランで使えない機能も出す。** 消すと「機能が無い」のか
 * 「見つけられていない」のかが本人に区別できず、必ず問い合わせになる
 * （ナビの方針と同じ）。使えないときは一言そえて、各画面へ送る。
 */
function FeatureCard({
  icon: Icon,
  title,
  to,
  available,
  children,
}: {
  icon: LucideIcon;
  title: string;
  to: string;
  available: boolean;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className="size-4 text-muted-foreground" aria-hidden />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm text-muted-foreground">
        {children}
        {!available ? (
          <p className="text-xs">
            ご利用中のプランには含まれていません。設定画面で、使えるプランと変更方法をご案内しています。
          </p>
        ) : null}
        <AppLink to={to} className="block text-primary underline-offset-4 hover:underline">
          設定の画面を開く
        </AppLink>
      </CardContent>
    </Card>
  );
}
